
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePageAPI } from '../hooks/usePageAPI';
import Pagination from './Pagination';
import { DateRangePicker, Stack } from 'rsuite';
import { CalendarEvent } from 'react-bootstrap-icons';
import { API_URL } from '../constants';
import { useTokens } from '../context/TokenContext';

export const FormLayout = ({ title, submitText, onCancel, onSubmit, children, loading }) => (
  <form onSubmit={onSubmit} className="container">
    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
      <div className="btn-toolbar mb-2 mb-md-0">
        <button type="button" className="btn sis-button me-3" onClick={onCancel} > <i className="bi bi-arrow-left"></i></button>
        <h1 className="fs-4 m-0 lh-base">{title}</h1>
      </div>

      <div className="btn-toolbar mb-2 mb-md-0">
        <button type="submit" className="btn btn-primary ms-2 btn-dark rounded-0" disabled={loading}>
          {submitText}
        </button>
      </div>
    </div>
    <div className="row">
      <div className="col-md-8">
        <div className='border p-4 pb-5 bg-white'>{children[0]}</div>
      </div>
      <div className="col-md-4">
        <div className='border p-4 bg-white'>{children[1]}</div>
      </div>
    </div>
  </form>
);

export const FormLayout2 = ({ title, submitText, onCancel, onSubmit, children, loading }) => (
  <form onSubmit={onSubmit} className="container">
    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
      <div className="btn-toolbar mb-2 mb-md-0">
        <button type="button" className="btn sis-button me-3" onClick={onCancel} > <i className="bi bi-arrow-left"></i></button>
        <h1 className="fs-4 m-0 lh-base">{title}</h1>
      </div>

      <div className="btn-toolbar mb-2 mb-md-0">
        <button type="submit" className="btn btn-primary ms-2 btn-dark rounded-0" disabled={loading}>
          {submitText}
        </button>
      </div>
    </div>
    <div className="row">
      <div className="col-md-8">
        {children[0]}
      </div>
      <div className="col-md-4">
        {children[1]}
      </div>
      <div className="col-md-12">
        {children[2]}
      </div>
    </div>
  </form>
);

export const PageList = ({ userToken, itemType, itemId, pageTitle, pageData, actionApplyKeyMap, columns, filterBy, columnKeyMap, onAddNew, onView, onEdit, onDelete, totalRows, statusChange, isReload, returns = () => {} }) => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const location = useLocation();
  const navigate = useNavigate();

  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      page: parseInt(params.get('page') || 1, 10),
      limit: parseInt(params.get('limit') || 20, 10),
      searchBy: params.get('searchBy') || 'title',
      searchValue: params.get('searchValue') || '',
      status: params.get('status') || ''
    };
  };

  const { page, limit, status, searchBy, searchValue } = getQueryParams();
  const [currentPage, setCurrentPage] = useState(page);
  const [currentLimit, setCurrentLimit] = useState(limit);
  const [currentStatus, setCurrentStatus] = useState(status);
  const [currentSearchBy, setCurrentSearchBy] = useState(searchBy);
  const [currentSearchValue, setCurrentSearchValue] = useState(searchValue);
  const [newParamsToNavigate, setNewParamsToNavigate] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [action, setAction] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updatePage, actionBulk } = usePageAPI();
  const totalPages = Math.max(1, Math.ceil(totalRows / currentLimit));
  const [formattedRange, setFormattedRange] = useState({ from: '', to: '' });

  // Helper to resolve nested keys (dot notation) in itemId or column mappings
  const resolveProp = (obj, path) => {
    if (!path || typeof path !== 'string') return undefined;
    if (!obj) return undefined;
    // support multiple types: 'id' or 'basic.id'
    const parts = path.split('.');
    let cur = obj;
    for (let i = 0; i < parts.length; i++) {
      if (cur === null || cur === undefined) return undefined;
      cur = cur[parts[i]];
    }
    return cur;
  };

  const formatDateToSQL = (date) => {
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const handleDateChange = (range) => {
    if (range && range.length === 2) {
      const [fromDate, toDate] = range;
      setCurrentSearchValue(JSON.stringify({ from: formatDateToSQL(fromDate), to: formatDateToSQL(toDate) }));
      setFormattedRange({ from: formatDateToSQL(fromDate), to: formatDateToSQL(toDate) });
    } else {
      setCurrentSearchValue(null);
    }
  };

  useEffect(() => {
    if (newParamsToNavigate && Object.keys(newParamsToNavigate).length > 0) {
      updateUrlParamsAndNavigate(newParamsToNavigate);
    }
    loading && setLoading(false);
  }, [newParamsToNavigate, loading]);

  useEffect(() => {
    returns(selectedIds);
  }, [selectedIds]);

  const handlePageChange = (type, page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleCheckboxChange = (event, id) => {
    if (event.target.checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    }
  };

  const handleBulkAction = async () => {
    if (!action) return toast.error('No action selected');
    if (selectedIds.length === 0) return toast.error('No records selected');

    if (action === 'delete' && !window.confirm("Are you sure you want to delete?")) return;

    const toastId = toast.loading('Applying...', { autoClose: false });
    setBulkActionLoading(true);

    try {
      const token = userToken || adminAuthToken;
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const response = await axios.post(
        `${API_URL}/${itemType}/bulk`,
        { action, ids: selectedIds },
        { headers }
      );

      const success = response.data?.success || false

      toast.update(toastId, {
        render: response.data?.message || (success ? 'Action applied successfully.' : 'Failed to apply action.'),
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      isReload(true);

      setSelectedIds([]);
      setAction('');
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Something went wrong.';

      toast.update(toastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(pageDataList.map((record) => record[itemId]));
    } else {
      setSelectedIds([]);
    }
  };

  const updateUrlParamsAndNavigate = (newParams) => {
    const currentParams = new URLSearchParams(location.search);
    const newUrlParams = new URLSearchParams();

    // Preserve all existing parameters
    for (const [key, value] of currentParams) {
      newUrlParams.set(key, value);
    }

    // Update only the parameters managed by PageList
    const managedParams = {
      page: currentPage,
      limit: currentLimit,
      status: currentStatus,
      searchBy: currentSearchBy,
      searchValue: currentSearchValue,
    };

    Object.keys(managedParams).forEach(key => {
      const value = newParams[key] !== undefined ? newParams[key] : managedParams[key];

      if (value !== undefined && value !== '') {
        newUrlParams.set(key, value);
      } else {
        newUrlParams.delete(key);
      }
    });

    // If searchValue is empty, remove both searchValue and searchBy
    if (!currentSearchValue || currentSearchValue === '') {
      newUrlParams.delete('searchValue');
      newUrlParams.delete('searchBy');
    }

    navigate(`?${newUrlParams.toString()}`);
  };

  const handleChange = (type, value) => {
    let newParams = {};

    switch (type) {
      case 'limit':
        setCurrentLimit(value);
        setCurrentPage(1);
        newParams.limit = value;
        break;
      case 'searchBy':
        setCurrentSearchBy(value);
        setCurrentPage(1);
        newParams.searchBy = value;
        break;
      case 'searchValue':
        setCurrentSearchValue(value);
        setCurrentPage(1);
        newParams.searchValue = value;
        break;
      case 'page':
        setCurrentPage(value);
        newParams.page = value;
        break;
      default:
        return;
    }

    if (type === 'limit' || type === 'searchBy' || type === 'searchValue') {
      delete newParams.page;
    }
    setNewParamsToNavigate(newParams);
  };

  const handleStatusChange = async (e, item, name, route) => {
    const previousStatus = item[`${statusChange?.name}`]; // Save the previous status directly from the item
    const newStatus = previousStatus === 1 ? 0 : 1; // Toggle between 1 and 0
    setLoading(true);

    const formDataToSubmit = new FormData();
    formDataToSubmit.append(name, newStatus);

    const toastId = toast.loading(`Updating status...`, { autoClose: false });

    try {
      const result = await updatePage({
        type: route,
        pageId: resolveProp(item, itemId) || item[itemId] || item.id,
        formDataToSubmit,
      });

      if (result) {
        // If the result status is successful, update the status to newStatus
        item[name] = newStatus; // Update the item's status
        toast.update(toastId, {
          render: `Status updated successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 500,
        });
      } else {
        // If the result isn't successful, revert the status
        item[name] = previousStatus; // Revert the status to the previous state
        toast.update(toastId, {
          render: result?.error || 'Failed to update status',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      // In case of error, revert to previous status and show error message
      item[name] = previousStatus; // Revert status to the previous state
      const errorMessage = error?.response?.data?.error || 'An unknown error occurred';
      toast.update(toastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      console.error('Error occurred:', error);
    } finally {
      setLoading(false);
    }
  };

  const pageDataList = pageData || [];

  return (
    <div>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
        <h1 className="fs-4 m-0 lh-base">{pageTitle}</h1>
        {onAddNew && (
          <button type="button" className="btn btn-outline-dark rounded-0" onClick={onAddNew}>
            <i className="bi bi-plus-square me-2"></i> Add New
          </button>
        )}
      </div>

      <div className="row align-items-center my-2">
        <div className="col-auto form-text">
          Total {totalRows || 0} records found, showing {Math.min((currentPage == '' ? 1 : currentPage - 1) * currentLimit + 1, totalRows || 0)} to {Math.min(currentPage * currentLimit, totalRows || 0)} of {totalRows || 0}
        </div>
        <div className="col-auto ms-auto">
          {filterBy &&
            <div className="row g-1 align-items-center">
              <div className="col-12 col-lg-auto">
                <label htmlFor="searchby" className="form-label m-0 me-2 form-text">Search By: </label>
              </div>
              <div className="col-auto">
                <select
                  className="form-select custom-select"
                  id="searchby"
                  value={currentSearchBy}
                  onChange={e => {
                    setCurrentSearchBy(e.target.value);
                    setCurrentSearchValue('');
                  }}
                >
                  {Object.keys(filterBy).map(key => (
                    <option key={key} value={filterBy[key]}>{key}</option>
                  ))}
                </select>
              </div>
              <div className="col-auto">
                {currentSearchBy === 'datetime' ? (
                  <DateRangePicker
                    format="dd MM yyyy hh:mm:ss"
                    showMeridiem
                    caretAs={CalendarEvent}
                    onChange={handleDateChange}
                  />
                ) : (
                  <>
                    <label htmlFor="search" className="visually-hidden">Search</label><input
                      type="text"
                      className="form-control custom-control-input"
                      id="search"
                      value={currentSearchValue}
                      onChange={e => setCurrentSearchValue(e.target.value)}
                      placeholder="Enter to Search" />
                  </>
                )}
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-primary"
                  onClick={() => handleChange('searchValue', currentSearchValue)}
                >
                  Search
                </button>
                {
                  currentSearchValue && (
                    <button
                      className="btn btn-outline-secondary ms-2"
                      onClick={() => {
                        setCurrentSearchValue('');
                        handleChange('searchValue', '');
                      }}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )
                }
              </div>
            </div>
          }
        </div>
      </div>

      <div className="row g-1 align-items-center my-2">
        <div className="col-auto">

          {actionApplyKeyMap &&
            <div className="row g-1 align-items-center">
              <div className="col-auto">
                <label htmlFor="search" className="visually-hidden">Search</label>
                <select
                  className="form-select custom-select"
                  id="action"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                >
                  <option key='' value=''>Bulk Action</option>
                  {Object.entries(actionApplyKeyMap).map(([key, val]) => (
                    <option key={val} value={val}>{key}</option>
                  ))}

                </select>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleBulkAction}
                  disabled={bulkActionLoading}
                >
                  {bulkActionLoading ? 'Applying...' : 'Apply'}
                </button>
              </div>
            </div>
          }
        </div>

        <div className="col-auto ms-auto page-select-limit">
          <div className="row g-1 align-items-center">
            <div className="col-auto ms-auto">
              <label htmlFor="limit" className="visually-hidden">Limit</label>
              <select
                className="form-select custom-select"
                id="limit"
                value={currentLimit}
                onChange={e => handleChange('limit', Number(e.target.value))}
              >
                {[10, 20, 50, 100, 250, 500].map(val => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-table table-responsive small border-top border-4">
        <table className={`table table-hover ${itemType}-table`}>
          <thead>
            <tr className='align-middle'>
              {actionApplyKeyMap &&
                <td className="align-middle ps-3">
                  <input
                    type="checkbox"
                    id="selectAll"
                    className="multiple-select custom-control-input"
                    onChange={handleSelectAll}
                    checked={selectedIds.length === pageDataList.length}
                  />
                </td>
              }

              {columns.map((column, index) => (
                <th scope="col" key={index}>{column}</th>
              ))}
              {(onView || onEdit || onDelete) && (
                <th scope="col" className="text-end">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageDataList.length > 0 ? pageDataList.map(item => (
              <tr key={resolveProp(item, itemId) || item[itemId] || item.id}>
                {actionApplyKeyMap &&
                  <td className="align-middle ps-3">
                    <input
                      type="checkbox"
                      id={`checkbox-${resolveProp(item, itemId) || item[itemId] || item.id}`}
                      className="single-select custom-control-input"
                      checked={selectedIds.includes(resolveProp(item, itemId) || item[itemId] || item.id)}
                      onChange={(e) => handleCheckboxChange(e, resolveProp(item, itemId) || item[itemId] || item.id)}
                    />
                  </td>
                }
                {columns.map((column, index) => (
                  <td key={index}>
                    {/* Conditionally render the checkbox input if columnKeyMap['Status'] is true */}
                    {column === 'Status' && columnKeyMap['Status'] === true ? (
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`flexSwitchCheckChecked-${resolveProp(item, itemId) || item[itemId] || item.id}`}
                          checked={!!item?.[statusChange?.name]}
                          onChange={(e) => handleStatusChange(e, item, statusChange?.name, statusChange?.route)}
                        />
                      </div>
                    ) : (
                      // Default rendering for other columns
                      column === '#' ? item.id :
                        typeof columnKeyMap[column] === 'function' ? columnKeyMap[column](item) :
                          item[columnKeyMap[column]]
                    )}
                  </td>
                ))}
                {(onView || onEdit || onDelete) ? (
                  <td className="text-end">
                    <div className="dropdown">
                      <button className="btn btn-sm rounded-0 btn-outline-danger p-0 px-1" data-bs-toggle="dropdown">
                        <i className="bi bi-three-dots"></i>
                      </button>
                      <div className="dropdown-menu dropdown-menu-right">
                        {onEdit && (
                          <div className="dropdown-item" onClick={() => onEdit(resolveProp(item, itemId) || item[itemId] || item.id)}>
                            Edit
                          </div>
                        )}
                        {onDelete && (
                          <div className="dropdown-item text-danger" onClick={() => onDelete(resolveProp(item, itemId) || item[itemId] || item.id)}>
                            Delete
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                ) : null}
              </tr>
            )) : (
              <tr>
                <td colSpan={columns.length + 1} className="text-center">No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          handleChange={handleChange}
        />
      </div>
    </div>
  );
};
